/**
 * Shared vocabulary and helpers for the two product forms (new + edit).
 *
 * These lived twice — once per page — and had already drifted (Edit's
 * textarea chrome used a different placeholder colour). AGENTS.md §7 rule 17:
 * the second usage is when it gets lifted.
 *
 * **`items.unit` is deliberately not authored here.** The form used to offer a
 * picker of Piece / Pack / Dozen / Metre / Kilogram, which was two problems in
 * one: *Pack* and *Dozen* are packaging, which a pricing entry's `kind` now
 * expresses (ADR-0051), and the vocabulary disagreed with the rest of the ERP
 * (`items.unit` defaults to `Nos`, `bill_lines` to `pcs`) while landing on
 * invoice lines permanently. The column keeps its `Nos` default, which is the
 * correct GST unit quantity code for countable goods.
 */

import { country } from "@flint/api-contract";

export interface SelectOption {
  label: string;
  value: string;
}

/**
 * Care instructions — a closed set. This was free text, which meant the same
 * instruction got stored a dozen ways and could never be filtered or grouped.
 *
 * Nothing server-side validates it: `care` is one key of the listing's generic
 * `attributes` jsonb, so this list is the only thing constraining the value.
 */
export const CARE_VALUES = ["Machine wash", "Hand wash", "Dry wash"] as const;
export type CareValue = (typeof CARE_VALUES)[number];

/** Derived from `CARE_VALUES` so the picker and the form schema can't drift apart. */
export const CARE_OPTIONS: readonly SelectOption[] = CARE_VALUES.map((v) => ({
  label: v,
  value: v,
}));

/**
 * Country-of-origin picker options.
 *
 * Unlike `CARE_OPTIONS`, the vocabulary is NOT authored here — it comes from
 * the `country` enum in `@flint/api-contract`, which is what the server
 * validates against. Deriving the options from the contract is what stops the
 * picker offering a value the API would reject. Same recipe as
 * `STATE_OPTIONS` in `vendors/components/vendor-form.tsx`.
 */
export const COUNTRY_OPTIONS: readonly SelectOption[] = country.options.map((c) => ({
  label: c,
  value: c,
}));

export type CountryValue = (typeof country.options)[number];

/**
 * What a new product is assumed to be made in. The operator can change it, and
 * the *edit* form never applies this — a saved null means nobody has said, and
 * inventing "India" on a product nobody touched would be putting words on a
 * customs declaration.
 */
export const DEFAULT_COUNTRY_OF_ORIGIN = "India";

/**
 * Resolve a saved care value to a listed one, or blank.
 *
 * Must run at form *hydration*, before react-hook-form sees the value: the
 * schema is a `z.enum`, so a legacy string reaching the resolver would fail
 * validation on a field the operator never touched and make the product
 * unsaveable until they picked a new value.
 */
export function normalizeCare(value: string | null | undefined): CareValue | "" {
  const v = value?.trim() ?? "";
  return (CARE_VALUES as readonly string[]).includes(v) ? (v as CareValue) : "";
}

/**
 * Resolve a saved country to a listed one, or blank. The `normalizeCare` rule,
 * for the same reason.
 *
 * `items.country_of_origin` is a plain varchar with no DB CHECK — the `country`
 * enum is the only constraint, and it constrains what the *API* accepts, not
 * what is already in the column. A row written by an import, or a name the enum
 * later drops, would otherwise reach the resolver, fail validation on a field
 * the operator never touched, and block the save entirely.
 */
export function normalizeCountryOfOrigin(value: string | null | undefined): CountryValue | "" {
  const v = value?.trim() ?? "";
  return (country.options as readonly string[]).includes(v) ? (v as CountryValue) : "";
}

/** Shared textarea chrome — mirrors the `Input` primitive (no Textarea in @flint/ui). */
export const TEXTAREA_CLASS =
  "w-full rounded-sm border border-line bg-paper px-3 py-2 text-ink placeholder:text-sm placeholder:text-placeholder transition-colors hover:border-line-2 focus-visible:outline-hidden focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100";

/** The merchandising free-text fields that persist as listing `attributes`. */
export interface AttributeValues {
  material: string;
  gsm: string;
  colour: string;
  care: string;
}

/** Drop blanks — an empty string is "not set", not an attribute worth storing. */
export function buildAttributes(v: AttributeValues): Record<string, string> {
  const a: Record<string, string> = {};
  if (v.material) a.material = v.material;
  if (v.gsm) a.gsm = v.gsm;
  if (v.colour) a.colour = v.colour;
  if (v.care) a.care = v.care;
  return a;
}
